import getConfig from 'next/config';

import Cookies, { Cookie } from 'universal-cookie';

import config from '../config';
import { serialize } from '../utils/objectTransformer';

const { publicRuntimeConfig } = getConfig();

export interface BaseResponse<T> {
  data: T;
  status: number;
}

class HttpRequestor {
  private readonly getAuthorizationBearerToken: () =>
    | { Authorization: string }
    | Record<string, unknown>;

  private readonly getAuthorizationApiKey: () =>
    | { Authorization: string }
    | Record<string, unknown>;

  private useFetch: () => any;

  private serialize: (body: any, contentType: string) => any;

  private readonly defaults: {
    mode: string;
    headers: {
      Accept: string;
      Vary: string;
      'Content-Type': string;
    };
    method: string;
  };

  constructor(
    public fetch: any,
    public baseUrl: string,
    public universalCookies: Cookie,
  ) {
    const serializers: any = {
      'application/x-www-form-urlencoded': serialize,
      'application/json': JSON.stringify,
    };

    this.useFetch = () => fetch;

    this.getAuthorizationBearerToken = () => {
      const token = this.universalCookies.get('token');
      if (token) {
        return { Authorization: `Bearer ${token}`, 'API-Key': token };
      }
      return {};
    };

    this.getAuthorizationApiKey = () => {
      const apiKey = this.universalCookies.get('api_key');
      if (apiKey) {
        return { 'Api-Key': apiKey };
      }
      return {};
    };

    this.serialize = (body: any, contentType: string) =>
      serializers[contentType] ? serializers[contentType](body) : body;

    this.universalCookies = universalCookies;
    this.baseUrl = baseUrl;
    this.defaults = {
      method: 'POST',
      mode: baseUrl ? 'cors' : 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Vary: 'Origin, Access-Control-Request-Headers, Access-Control-Request-Method',
        ...this.getAuthorizationApiKey(),
        ...this.getAuthorizationBearerToken(),
      },
    };
  }

  public post<T>(
    url: string,
    body?: any,
    options?: any,
  ): Promise<BaseResponse<T>> {
    return this.execute<T>(url, {
      method: 'POST',
      body,
      ...options,
    });
  }

  public put<T>(
    url: string,
    body: any,
    options?: any,
  ): Promise<BaseResponse<T>> {
    return this.execute(url, {
      method: 'PUT',
      body,
      ...options,
    });
  }

  public get<T>(url: string, options?: any): Promise<BaseResponse<T>> {
    return this.execute<T>(url, {
      method: 'GET',
      ...options,
    });
  }

  public delete(url: string, options?: any): Promise<any> {
    return this.execute(url, {
      method: 'DELETE',
      ...options,
    });
  }

  public execute<T>(url: string, options?: any): Promise<BaseResponse<T>> {
    // query: get Parameters
    const { query, ...finalOptions } = options;
    const formattedURL = !query ? url : `${url}?${serialize(query)}`;
    const mergedOptions = {
      ...this.defaults,
      ...finalOptions,
      headers: {
        ...this.defaults.headers,
        ...(options && options.headers),
        ...this.getAuthorizationApiKey(),
        ...this.getAuthorizationBearerToken(),
      },
    };

    const { body, headers } = mergedOptions;

    return this.useFetch()(`${this.baseUrl}${formattedURL}`, {
      ...mergedOptions,
      body: this.serialize(body, headers['Content-Type']),
    })
      .then(async (response: any) => {
        if (config.isDebugMode) {
          console.info(
            '[services httpRequestor execute]',
            `url:${this.baseUrl}${formattedURL}`,
            response,
          );
        }
        const contentType = response.headers.get('content-type');

        if (!contentType) {
          return { status: response.status };
        }
        if (
          process.browser &&
          contentType.includes('application/octet-stream')
        ) {
          return response.blob();
        }

        if (contentType.includes('application/pdf')) {
          return response.buffer();
        }

        const { status } = response;
        if (
          status.toString().startsWith(4) ||
          status.toString().startsWith(5)
        ) {
          // const { error } = await response.json();
          const error = 'Error';

          return {
            status,
            error,
          };
        }

        const data = await response.json();
        return {
          status,
          data,
        } as BaseResponse<T>;
      })
      .catch((error: any) => {
        console.error('[services httpRequestor execute Error]', error);
        if (!process.browser) {
          throw error;
        }
        return error;
      });
  }
}

export const fetcher = (cookies?: string) =>
  new HttpRequestor(
    fetch,
    process.browser ? '/api' : publicRuntimeConfig.serverUrl,
    new Cookies(cookies || (process.browser ? document.cookie : undefined)),
  );

export const fetcherStack9 = (apiKey: string, apiUrl: string) =>
  new HttpRequestor(fetch, apiUrl, new Cookies({ api_key: apiKey }));

export const fetcherRecapthca = () =>
  new HttpRequestor(fetch, publicRuntimeConfig.recaptchaUrl, new Cookies({}));

export default HttpRequestor;
